"use client";

import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavSection, resolveRouteFromPathname, ROUTE_REGISTRY } from "@/data/routeRegistry";

const sectionLabels: Partial<Record<NavSection, string>> = {
  [NavSection.Dashboard]: "dataAndAnalytics",
  [NavSection.Communications]: "communications",
  [NavSection.General]: "general",
};

/** Use the route catalog for nested pages; never expose IDs or guess labels from URLs. */
export default function AppTopbar({ children }: { children?: ReactNode }) {
  const pathname = usePathname() ?? "";
  const { t } = useTranslation("routes");
  const route = resolveRouteFromPathname(pathname);
  const parent = route?.nav ? route : ROUTE_REGISTRY
    .filter((entry) => entry.nav && entry.path !== "/" && pathname.startsWith(`${entry.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0] ?? ROUTE_REGISTRY.find(
      (entry) => entry.nav?.activeSegments?.includes(pathname.split("/")[1])
    );
  const sectionKey = parent?.nav && sectionLabels[parent.nav.section];
  const labelKey = route?.breadcrumbLabelKey ?? route?.search?.labelKey ?? route?.nav?.labelKey;
  const label = labelKey ? t(labelKey) : undefined;

  return (
    <header className="sticky top-16 z-20 flex h-14 shrink-0 items-center justify-between gap-4 border-b border-border bg-panel px-4 md:top-0 lg:px-[max(72px,calc((100%-80rem)/2+72px))]" data-testid="app-topbar">
      <nav aria-label={t("breadcrumb")} className="min-w-0 text-[13px]">
        <ol className="flex min-w-0 items-center gap-2">
          {sectionKey && <li className="hidden shrink-0 text-500 sm:block">{t(sectionKey)}</li>}
          {sectionKey && <li aria-hidden className="hidden text-400 sm:block">/</li>}
          {label && <li aria-current="page" className="truncate text-900">{label}</li>}
        </ol>
      </nav>
      {children}
    </header>
  );
}
